import React, { FC, useEffect, useMemo } from "react";
import { Card, Col, Row, Statistic } from "antd";
import { IProject } from "../../../models/project";
import MultiSeriesLineChart from "./LineChart";

interface IProps {
    topology: string;
    project: IProject;
}

const Results: FC<IProps> = ({ topology, project }) => {
    const [imData, setImData] = React.useState<any[]>([]);
    const [srmData, setSrmData] = React.useState<any[]>([]);
    const [smpmData, setSmpmData] = React.useState<any[]>([]);

    useEffect(() => {
        setImData([]);
        setSrmData([]);
        setSmpmData([]);

        if (!project?.results) return;

        // --------------------------
        // Induction Machine dataset
        // --------------------------
        if (topology === "Induction Machine") {
            const speeds = (project.results.speed ?? []) as number[];

            const next = speeds.map((speed, index) => {
                const fallback = Math.max(0, 15 * Math.sin((speed / 1500) * Math.PI));

                return {
                    speed,
                    airgap: project.results.torque?.[index] ?? fallback,
                    shaft: project.results.torque_shaft?.[index] ?? fallback,
                    current: project.results.current?.[index] ?? fallback,
                    efficiency: project.results.efficiency?.[index] ?? fallback,
                    powerFactor: project.results.pf?.[index] ?? fallback,
                    statorLoss: project.results.P_stator_copper?.[index] ?? fallback,
                    rotorLoss: project.results.P_rotor_copper?.[index] ?? fallback,
                    coreLoss: project.results.P_core?.[index] ?? 40 + 0.02 * speed,
                    windageLoss: project.results.P_windage?.[index] ?? 20,
                    inputPower:
                        project.results.P_in?.[index] ??
                        (2000 + 1.5 * speed - 0.001 * speed ** 2),
                    outputPower:
                        project.results.P_out?.[index] ??
                        (1500 + 1.2 * speed - 0.0012 * speed ** 2),
                };
            });

            setImData(next);
            return;
        }

        // -----------------------------------
        // Synchronous Reluctance Machine dataset
        // -----------------------------------
        if (topology === "Synchronous Reluctance Machine") {
            const xs = (project.results.magnetic_potential_stator_x ?? []) as number[];

            const next = xs.map((x, index) => ({
                magnetic_potential_stator_x: x,
                magnetic_potential_rotor_x: project.results.magnetic_potential_rotor_x?.[index] ?? 0,
                magnetic_potential_stator_y: project.results.magnetic_potential_stator_y?.[index] ?? 0,
                magnetic_potential_rotor_y: project.results.magnetic_potential_rotor_y?.[index] ?? 0,
                magnetic_flux_density_x: project.results.magnetic_flux_density_x?.[index] ?? 0,
                magnetic_flux_density_y: project.results.magnetic_flux_density_y?.[index] ?? 0,
                electromagnetic_torque_x: project.results.electromagnetic_torque_x?.[index] ?? 0,
                electromagnetic_torque_y: project.results.electromagnetic_torque_y?.[index] ?? 0,
                flux_density_harmonic_order_x: project.results.flux_density_harmonic_order_x?.[index] ?? 0,
                flux_density_harmonic_magnitude_y: project.results.flux_density_harmonic_magnitude_y?.[index] ?? 0,
                electromagnetic_torque_harmonic_order_x: project.results.electromagnetic_torque_harmonic_order_x?.[index] ?? 0,
                electromagnetic_torque_harmonic_magnitude_y: project.results.electromagnetic_torque_harmonic_magnitude_y?.[index] ?? 0,
                phase_current_a_x: project.results.phase_current_a_x?.[index] ?? 0,
                phase_current_a_y: project.results.phase_current_a_y?.[index] ?? 0,
                phase_current_b_x: project.results.phase_current_b_x?.[index] ?? 0,
                phase_current_b_y: project.results.phase_current_b_y?.[index] ?? 0,
                phase_current_c_x: project.results.phase_current_c_x?.[index] ?? 0,
                phase_current_c_y: project.results.phase_current_c_y?.[index] ?? 0,
                flux_linkage_a_x: project.results.flux_linkage_a_x?.[index] ?? 0,
                flux_linkage_a_y: project.results.flux_linkage_a_y?.[index] ?? 0,
                flux_linkage_b_x: project.results.flux_linkage_b_x?.[index] ?? 0,
                flux_linkage_b_y: project.results.flux_linkage_b_y?.[index] ?? 0,
                flux_linkage_c_x: project.results.flux_linkage_c_x?.[index] ?? 0,
                flux_linkage_c_y: project.results.flux_linkage_c_y?.[index] ?? 0,
            }));

            setSrmData(next);
        }

        if (topology === "Surface Mounted Permanent Magnet Machine") {
            const xs = (project.results.theta ?? []) as number[];

            const next = xs.map((x, index) => ({
                theta: x,
                lambda_A_oc: project.results.lambda_A_oc?.[index] ?? 0,
                lambda_B_oc: project.results.lambda_B_oc?.[index] ?? 0,
                lambda_C_oc: project.results.lambda_C_oc?.[index] ?? 0,
                e_A_oc: project.results.e_A_oc?.[index] ?? 0,
                e_B_oc: project.results.e_B_oc?.[index] ?? 0,
                e_C_oc: project.results.e_C_oc?.[index] ?? 0,
                Bg: project.results.Bg?.[index] ?? 0,
                Bg_oc: project.results.Bg_oc?.[index] ?? 0,
                i_A: project.results.i_A?.[index] ?? 0,
                i_B: project.results.i_B?.[index] ?? 0,
                i_C: project.results.i_C?.[index] ?? 0,
                lambda_A: project.results.lambda_A?.[index] ?? 0,
                lambda_B: project.results.lambda_B?.[index] ?? 0,
                lambda_C: project.results.lambda_C?.[index] ?? 0,
                e_A: project.results.e_A?.[index] ?? 0,
                e_B: project.results.e_B?.[index] ?? 0,
                e_C: project.results.e_C?.[index] ?? 0,
                back_emf: project.results.back_emf?.[index] ?? 0,
                oc_back_emf: project.results.oc_back_emf?.[index] ?? 0,
            }));

            setSmpmData(next);
        }
    }, [project, topology]);

    const isIM = topology === "Induction Machine";
    const isSRM = topology === "Synchronous Reluctance Machine";
    const isSMPM = topology === "Surface Mounted Permanent Magnet Machine";

    return (
        <>
            {/* ------------------ IM STATISTICS ------------------ */}
            {isIM && (
                <Row gutter={24}>
                    <Col span={5}>
                        <Card variant="borderless">
                            <Statistic
                                title="Breakdown Airgap Torque [Nm]"
                                value={project.results.breakdown_air_gap_torque}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>
                    <Col span={5}>
                        <Card variant="borderless">
                            <Statistic
                                title="Breakdown Shaft Torque [Nm]"
                                value={project.results.breakdown_shaft_torque}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>

                    <Col span={5}>
                        <Card variant="borderless">
                            <Statistic
                                title="Starting Torque [Nm]"
                                value={project.results.starting_torque}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>

                    <Col span={5}>
                        <Card variant="borderless">
                            <Statistic
                                title="Maximum Efficiency [%]"
                                value={project.results.maximum_efficiency}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                                suffix="%"
                            />
                        </Card>
                    </Col>

                    <Col span={4}>
                        <Card variant="borderless">
                            <Statistic
                                title="Maximum Power Factor [-]"
                                value={project.results.maximum_power_factor}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* ------------------ SRM STATISTICS ------------------ */}
            {isSRM && (
                <Row gutter={24}>
                    <Col span={4}>
                        <Card variant="borderless">
                            <Statistic
                                title="Mean Torque [Nm]"
                                value={project.results.mean_torque_nm}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>
                    <Col span={4}>
                        <Card variant="borderless">
                            <Statistic
                                title="Torque Ripple [%]"
                                value={project.results.torque_ripple_percent}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>

                    <Col span={4}>
                        <Card variant="borderless">
                            <Statistic
                                title="Inductance d-axis [mH]"
                                value={project.results.inductance_d_axis_mh}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>

                    <Col span={4}>
                        <Card variant="borderless">
                            <Statistic
                                title="Inductance q-axis [mH]"
                                value={project.results.inductance_q_axis_mh}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>

                    <Col span={4}>
                        <Card variant="borderless">
                            <Statistic
                                title="Saliency [-]"
                                value={project.results.saliency}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>

                    <Col span={4}>
                        <Card variant="borderless">
                            <Statistic
                                title="Internal Power Factor [-]"
                                value={project.results.internal_power_factor}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>
                </Row>
            )}

            {/* ------------------ SMPM STATISTICS ------------------ */}
            {isSMPM && (
                <Row gutter={24}>
                    <Col span={4}>
                        <Card variant="borderless">
                            <Statistic
                                title="Mean Torque [Nm]"
                                value={project.results.mean_torque_nm}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>
                    <Col span={4}>
                        <Card variant="borderless">
                            <Statistic
                                title="Flux Linkage [mWb]"
                                value={project.results.flux_linkage}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>

                    <Col span={4}>
                        <Card variant="borderless">
                            <Statistic
                                title="Back-EMF [V]"
                                value={project.results.back_emf}
                                precision={2}
                                valueStyle={{ color: "#3f8600" }}
                            />
                        </Card>
                    </Col>

                    {
                        project.results.oc &&
                        <>
                            <Col span={4}>
                                <Card variant="borderless">
                                    <Statistic
                                        title="O.C Back-EMF [V]"
                                        value={project.results.oc_back_emf}
                                        precision={2}
                                        valueStyle={{ color: "#3f8600" }}
                                    />
                                </Card>
                            </Col>

                            <Col span={4}>
                                <Card variant="borderless">
                                    <Statistic
                                        title="O.C Flux Linkage [mWb]"
                                        value={project.results.oc_flux_linkage}
                                        precision={2}
                                        valueStyle={{ color: "#3f8600" }}
                                    />
                                </Card>
                            </Col>
                        </>
                    }


                </Row>
            )}

            {/* ------------------ IM CHARTS ------------------ */}
            {isIM && (
                <div style={{ marginTop: 24 }}>
                    <Row gutter={[24, 24]}>
                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Torque"
                                    data={imData}
                                    xKey="speed"
                                    xLabel="Speed [rpm]"
                                    xName="Speed" 
                                    yLabel="Torque [Nm]"
                                    series={[
                                        { key: "airgap", label: "Airgap Torque" },
                                        { key: "shaft", label: "Shaft Torque" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Phase Current"
                                    data={imData}
                                    xKey="speed"
                                    xLabel="Speed [rpm]"
                                    xName="Speed" 
                                    yLabel="Phase Current [A]"
                                    series={[{ key: "current", label: "Phase Current" }]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Efficiency"
                                    data={imData}
                                    xKey="speed"
                                    xLabel="Speed [rpm]"
                                    xName="Speed" 
                                    yLabel="Efficiency [%]"
                                    series={[{ key: "efficiency", label: "Efficiency" }]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Power Factor"
                                    data={imData}
                                    xKey="speed"
                                    xLabel="Speed [rpm]"
                                    xName="Speed" 
                                    yLabel="Power Factor [-]"
                                    series={[{ key: "powerFactor", label: "Power Factor" }]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Losses"
                                    data={imData}
                                    xKey="speed"
                                    xLabel="Speed [rpm]"
                                    xName="Speed" 
                                    yLabel="Losses [W]"
                                    series={[
                                        { key: "statorLoss", label: "Stator Copper Losses" },
                                        { key: "rotorLoss", label: "Rotor Copper Losses" },
                                        { key: "coreLoss", label: "Core Losses" },
                                        { key: "windageLoss", label: "Windage Losses" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Power"
                                    data={imData}
                                    xKey="speed"
                                    xLabel="Speed [rpm]"
                                    xName="Speed" 
                                    yLabel="Power [W]"
                                    series={[
                                        { key: "inputPower", label: "Input Power" },
                                        { key: "outputPower", label: "Output Power" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>
                    </Row>
                </div>
            )}

            {/* ------------------ SRM CHARTS (FIXED KEYS) ------------------ */}
            {isSRM && (
                <div style={{ marginTop: 24 }}>
                    <Row gutter={[24, 24]}>
                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Electromagnetic Torque"
                                    data={srmData.filter(d => 
                                        d.electromagnetic_torque_y != null && 
                                        d.electromagnetic_torque_y !== 0
                                    )}
                                    xKey="electromagnetic_torque_x"
                                    xLabel="Rotor Position [e-deg]"
                                    xName="Rotor Position"
                                    yLabel="Torque [Nm]"
                                    series={[
                                        { key: "electromagnetic_torque_y", label: "Electromagnetic Torque [Nm]" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Magnetic FLux Density"
                                    data={srmData}
                                    xKey="magnetic_flux_density_x"
                                    xLabel="Airgap Periphery [degree]"
                                    xName="Airgap Periphery"
                                    yLabel="Flux Density [T]"
                                    series={[
                                        { key: "magnetic_flux_density_y", label: "FLux Density [T]" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Phase Currents"
                                    data={srmData.filter(d => 
                                        d.phase_current_a_y != null && 
                                        d.phase_current_a_y !== 0
                                    )}
                                    xKey="phase_current_a_x"
                                    xLabel="Rotor Position [e-deg]"
                                    xName="Rotor Position"
                                    yLabel="Phase Current [A]"
                                    series={[
                                        { key: "phase_current_a_y", label: "Phase A" },
                                        { key: "phase_current_b_y", label: "Phase B" },
                                        { key: "phase_current_c_y", label: "Phase C" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Flux Linkages"
                                    data={srmData.filter(d => 
                                        d.flux_linkage_a_y != null && 
                                        d.flux_linkage_a_y !== 0
                                    )}
                                    xKey="flux_linkage_a_x"
                                    xLabel="Rotor Position [e-deg]"
                                    xName="Rotor Position"
                                    yLabel="Flux Linkage [Wb]"
                                    series={[
                                        { key: "flux_linkage_a_y", label: "Phase A" },
                                        { key: "flux_linkage_b_y", label: "Phase B" },
                                        { key: "flux_linkage_c_y", label: "Phase C" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Magnetic Potential"
                                    data={srmData}
                                    xKey="magnetic_potential_stator_x"
                                    xLabel="Airgap Periphery [degree]"
                                    xName="Airgap Periphery"
                                    yLabel="Magnetic Potential [AV]"
                                    series={[
                                        {
                                            key: "magnetic_potential_stator_y",
                                            label: "Stator Magnetic Potential",
                                        },
                                        {
                                            key: "magnetic_potential_rotor_y",
                                            label: "Rotor Magnetic Potential",
                                        },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Flux Density Harmonic"
                                    data={srmData.filter(d => 
                                        d.flux_density_harmonic_magnitude_y != null && 
                                        d.flux_density_harmonic_magnitude_y !== 0
                                    )}
                                    xKey="flux_density_harmonic_order_x"
                                    xLabel="Harmonic order"
                                    xName="Harmonic order"
                                    yLabel="Magnetic Flux Density [T]"
                                    chartType="bar" 
                                    series={[
                                        { key: "flux_density_harmonic_magnitude_y", label: "Magnetic Flux Density [T]" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Electromagnetic Torque Harmonic"
                                    data={srmData.filter(d => 
                                        d.electromagnetic_torque_harmonic_magnitude_y != null && 
                                        d.electromagnetic_torque_harmonic_magnitude_y !== 0
                                    )}
                                    xKey="electromagnetic_torque_harmonic_order_x"
                                    xLabel="Harmonic order"
                                    xName="Harmonic order"
                                    yLabel="Torque [Nm]"
                                    chartType="bar"
                                    series={[
                                        { key: "electromagnetic_torque_harmonic_magnitude_y", label: "Electromagnetic Torque [Nm]" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        
                    </Row>
                </div>
            )}

            {/* ------------------ SMPM CHARTS (FIXED KEYS) ------------------ */}
            {isSMPM && (
                <div style={{ marginTop: 24 }}>
                    <Row gutter={[24, 24]}>
                        {project.results.oc && <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Flux Linkage - Open-Circuit"
                                    data={smpmData}
                                    xKey="theta"
                                    xLabel="Rotor Position [e-deg]"
                                    yLabel="Flux Linkage [mWb]"
                                    series={[
                                        {
                                            key: "lambda_A_oc",
                                            label: "Phase A",
                                        },
                                        {
                                            key: "lambda_B_oc",
                                            label: "Phase B",
                                        },
                                        {
                                            key: "lambda_C_oc",
                                            label: "Phase C",
                                        }
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>}

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Magnetic FLux Density"
                                    data={smpmData}
                                    xKey="theta"
                                    xLabel="Air Gap Flux Density"
                                    yLabel="Air Gap Flux Density [T]"
                                    series={project.results.oc ? [
                                        { key: "Bg", label: "On-Load" },
                                        { key: "Bg_oc", label: "Open-Circuit" },
                                    ] : [
                                        { key: "Bg", label: "Air Gap Flux Density [T]" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Phase Current"
                                    data={smpmData}
                                    xKey="theta"
                                    xLabel="Rotor Position [e-deg]"
                                    yLabel="Phase Current [A]"
                                    series={[
                                        { key: "i_A", label: "Phase A" },
                                        { key: "i_B", label: "Phase B" },
                                        { key: "i_C", label: "Phase C" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Flux Linkage - On-Load"
                                    data={smpmData}
                                    xKey="theta"
                                    xLabel="Rotor Position [e-deg]"
                                    yLabel="Flux Linkage [mWb]"
                                    series={[
                                        { key: "lambda_A", label: "Phase A" },
                                        { key: "lambda_B", label: "Phase B" },
                                        { key: "lambda_C", label: "Phase C" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>

                        <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Back-EMF - On-Load"
                                    data={smpmData}
                                    xKey="theta"
                                    xLabel="Rotor Position [e-deg]"
                                    yLabel="Induced Voltage [V]"
                                    series={[
                                        { key: "e_A", label: "Phase A" },
                                        { key: "e_B", label: "Phase B" },
                                        { key: "e_C", label: "Phase C" },
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>


                        {project.results.oc && <Col span={12}>
                            <Card>
                                <MultiSeriesLineChart
                                    title="Back-EMF - Open-Circuit"
                                    data={smpmData}
                                    xKey="theta"
                                    xLabel="Rotor Position [e-deg]"
                                    yLabel="Induced Voltage [V]"
                                    series={[
                                        {
                                            key: "e_A_oc",
                                            label: "Phase A",
                                        },
                                        {
                                            key: "e_B_oc",
                                            label: "Phase B",
                                        },
                                        {
                                            key: "e_C_oc",
                                            label: "Phase C",
                                        }
                                    ]}
                                    darkMode
                                />
                            </Card>
                        </Col>}

                    </Row>
                </div>
            )}
        </>
    );
};

export default Results;